import type { ColumnDef } from '@tanstack/react-table';
import {
  Badge,
  DataTableHierarchyCell,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  createAppColumnHelper,
  getGroupSelectionColumn,
  type DataTableExternalSelectionAdapter,
  type DataTableFeatures
} from 'design-system';
import { LockKeyholeIcon } from 'lucide-react';

import { formatNumberWithPrecision } from '@/shared/lib/format/formatNumberWithPrecision';

import type { CommoditiesTableRowType } from '../../types';

export type CommodityDataRow = {
  kind: 'commodity';
  id: string;
  value: CommoditiesTableRowType;
  restricted: boolean;
};

export type CommoditySectionRow = {
  kind: 'section';
  id: string;
  name: string;
  subRows: CommodityDataRow[];
};

export type CommodityTableRow = CommoditySectionRow | CommodityDataRow;

type CommoditiesTableColumnOptions = {
  disableSelection: boolean;
  formatDate: (date: string) => string;
  isLoading: boolean;
  selectionAdapter: DataTableExternalSelectionAdapter<CommodityTableRow>;
  showDiff: boolean;
  showDiffChange: boolean;
  showLast: boolean;
  showLastChange: boolean;
};

const columnHelper = createAppColumnHelper<CommodityTableRow>();

function renderRestrictedValue() {
  const badge = (
    <Badge
      aria-label='Restricted market data'
      render={
        <button
          aria-label='Restricted market data'
          type='button'
        />
      }
      variant='neutralOutline'
    >
      <LockKeyholeIcon />
      <span className='sr-only'>Restricted</span>
    </Badge>
  );

  return (
    <Tooltip>
      <TooltipTrigger render={badge} />
      <TooltipContent>
        <span>You don&apos;t have access to this.</span>
        <a
          className='font-bold underline'
          href='https://www.general-index.com/contact-us'
          rel='noopener noreferrer'
          target='_blank'
        >
          Request access
        </a>
      </TooltipContent>
    </Tooltip>
  );
}

function renderLoadingCell(label: string) {
  return (
    <output
      aria-label={label}
      className='block w-full'
    >
      <Skeleton className='ml-auto h-5 w-16' />
    </output>
  );
}

function renderValue({
  row,
  value,
  isLoading,
  label,
  change = false
}: {
  row: CommodityTableRow;
  value: string | null;
  isLoading: boolean;
  label: string;
  change?: boolean;
}) {
  if (row.kind === 'section') return null;
  if (isLoading && !value) return renderLoadingCell(label);
  if (row.restricted) return renderRestrictedValue();
  if (!value) return null;

  const formatted = formatNumberWithPrecision(value);
  if (!change) return formatted;

  const numericValue = Number(value);
  let tone = 'text-muted-foreground';
  if (numericValue > 0) tone = 'text-gx-success';
  if (numericValue < 0) tone = 'text-gx-failure-light';
  return <span className={tone}>{formatted}</span>;
}

export function getCommoditiesTableColumns({
  disableSelection,
  formatDate,
  isLoading,
  selectionAdapter,
  showDiff,
  showDiffChange,
  showLast,
  showLastChange
}: CommoditiesTableColumnOptions): ColumnDef<DataTableFeatures, CommodityTableRow>[] {
  const shortTitleColumn = columnHelper.display({
    id: 'shortTitle',
    header: 'Short Title',
    cell: ({ row }) => (
      <DataTableHierarchyCell row={row}>
        {row.original.kind === 'section' ? (
          <span className='font-semibold'>{row.original.name}</span>
        ) : (
          row.original.value.shortTitle
        )}
      </DataTableHierarchyCell>
    )
  });

  const dateColumn = columnHelper.display({
    id: 'date',
    header: 'Date',
    meta: { align: 'end' },
    cell: ({ row }) => {
      if (row.original.kind === 'section') return null;
      const date = row.original.value.date;
      if (isLoading && !date) return renderLoadingCell('Loading commodity date');
      return date ? formatDate(date) : null;
    }
  });

  const lastColumn = columnHelper.display({
    id: 'last',
    header: 'Last',
    meta: { align: 'end' },
    cell: ({ row }) =>
      renderValue({
        row: row.original,
        value: row.original.kind === 'commodity' ? row.original.value.last : null,
        isLoading,
        label: 'Loading Last'
      })
  });

  const lastChangeColumn = columnHelper.display({
    id: 'lastChange',
    header: 'Change',
    meta: { align: 'end' },
    cell: ({ row }) =>
      renderValue({
        row: row.original,
        value: row.original.kind === 'commodity' ? row.original.value.delta : null,
        isLoading,
        label: 'Loading Last change',
        change: true
      })
  });

  const diffColumn = columnHelper.display({
    id: 'diff',
    header: 'Diff',
    meta: { align: 'end' },
    cell: ({ row }) =>
      renderValue({
        row: row.original,
        value: row.original.kind === 'commodity' ? row.original.value.diff : null,
        isLoading,
        label: 'Loading Diff'
      })
  });

  const diffChangeColumn = columnHelper.display({
    id: 'diffChange',
    header: 'Change',
    meta: { align: 'end' },
    cell: ({ row }) =>
      renderValue({
        row: row.original,
        value: row.original.kind === 'commodity' ? row.original.value.diffDelta : null,
        isLoading,
        label: 'Loading Diff change',
        change: true
      })
  });

  return columnHelper.columns([
    ...(disableSelection
      ? []
      : [
          getGroupSelectionColumn<CommodityTableRow>({
            selection: { type: 'external', adapter: selectionAdapter }
          })
        ]),
    shortTitleColumn,
    dateColumn,
    ...(showLast ? [lastColumn] : []),
    ...(showLastChange ? [lastChangeColumn] : []),
    ...(showDiff ? [diffColumn] : []),
    ...(showDiffChange ? [diffChangeColumn] : [])
  ]);
}
